import { capitalize, luminance } from "../utils";
import moment from 'moment';
import { WorkflowFolder } from "../interfaces";
import { DotsVerticalIcon, FolderIcon, UserIcon } from "@heroicons/react/solid";
import { Popover } from "@headlessui/react";
import useUser from "../hooks/useUser";
import { useModal } from "../contexts";
import FolderDeleteModal from "./FolderDeleteModal";
import { PencilIcon, TrashIcon } from "@heroicons/react/outline";
import FolderModal from "./FolderModal";
import { deleteData, putData } from "../backend";
import { KeyedMutator } from "swr";
import { useTranslation } from "react-i18next";

interface Props {
  folder: WorkflowFolder;
  mutateFolders: KeyedMutator<Array<WorkflowFolder>>;
  onChange: () => void;
  onClick: () => void;
}

function WorkflowFolderRow(props: Props) {
  const { user, hasPermission } = useUser();
  const { folder } = props;
  const { t } = useTranslation();

  const { openModal, closeModal } = useModal();

  function handleUpdateFolder(folder: WorkflowFolder) {
    closeModal();
    props.mutateFolders((prev) => prev!.map(f => f.id === folder.id ? folder : f), { revalidate: false });

    putData(`/workflows/folders/${folder.id}/`, folder)
      .then(() => { props.mutateFolders((prev) => prev!.map(f => f.id === folder.id ? folder : f)); props.onChange() })
      .catch(console.error);
  }

  function handleDelteFolder(folderId: string) {
    closeModal();
    props.mutateFolders((prev) => prev!.filter(f => f.id !== folder.id), { revalidate: false });

    deleteData(`/workflows/folders/${folderId}/`)
      .then(() => { props.mutateFolders(); props.onChange() })
      .catch(console.error);
  }

  function openEditFolderModal() {
    openModal(<FolderModal folder={folder} updateFolder={handleUpdateFolder} />);
  }

  function openDeleteFolderModal() {
    openModal(<FolderDeleteModal folder={folder} deleteFolder={handleDelteFolder} />);
  }

  return (
    <div className="flex items-center justify-between text-sm border-b last:border-0 border-gray-200">
      <input type="checkbox" checked={false} disabled className='h-4 w-4 cursor-pointer hover:border-gray-300 my-auto mr-4 invisible' />

      <div className="flex items-center h-full py-4 w-1/2 pr-10 group cursor-pointer" onClick={() => props.onClick()}>
        <div className="flex justify-center items-center w-10 h-10 mr-4 rounded select-none relative" style={{ color: folder.color }}>
          <FolderIcon className="w-10 h-10" />
          {folder.team &&
            <UserIcon className="absolute w-4 h-4 bottom-2.5 right-1.5" style={{ color: luminance(folder.color) > 0 ? "white" : "black" }} />
          }
        </div>
        <div className="-mx-2 px-2 font-semibold text-gray-600 group-hover:text-blue overflow-ellipsis overflow-hidden whitespace-nowrap">
          {folder.name}
        </div>
      </div>
      <div className="flex items-center justify-between w-1/2">

        <div className="flex items-center justify-start font-gilroy font-semibold text-sm text-gray-600 w-full">
          {folder.children === 0
            ? <span className="text-gray-400">Empty</span>
            : <span className="text-gray-600">{folder.children} Workflow{folder.children > 1 ? "s" : ""}</span>
          }
        </div>

        <div className="flex items-center justify-start font-gilroy font-semibold text-sm text-gray-600 w-full">
          <span className="text-gray-400">{t("Created")}</span>&nbsp;{capitalize(moment(folder.createdAt).fromNow())}
        </div>

        <div className="flex items-center justify-between ml-auto ">
          <div className="flex-shrink-0" onClick={e => e.stopPropagation()}>
            <Popover className="relative" onClick={(e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); }}>

              <Popover.Button className={`flex justify-center items-center w-8 h-8 shadow rounded outline-none focus-visible:outline-black ${false ? "cursor-not-allowed text-gray-400" : "cursor-pointer bg-white hover:text-blue"}`} disabled={false}>
                <DotsVerticalIcon className="w-4 h-4 focus-visible:outline-red focus-visible:outline" tabIndex={-1} />
              </Popover.Button>

              <Popover.Panel className="absolute mt-1 right-0 z-20 flex flex-col w-42 bg-white border rounded shadow overflow-y-auto">

                {/* Fallback option */}
                <Popover.Button className="hidden last:flex items-center font-medium text-xs text-gray-600 w-full px-4 py-2 whitespace-nowrap">
                  No options available
                </Popover.Button>

                {hasPermission(user?.activeTeam, "workflows.edit") &&
                  <Popover.Button className="flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap" onClick={openEditFolderModal}>
                    <PencilIcon className="w-4 h-4 mr-2" /> Edit
                  </Popover.Button>
                }

                {hasPermission(user?.activeTeam, "workflows.delete") &&
                  <Popover.Button className="flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap" onClick={openDeleteFolderModal}>
                    <TrashIcon className="w-4 h-4 mr-2" /> Delete
                  </Popover.Button>
                }

              </Popover.Panel>
            </Popover>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorkflowFolderRow;
